import { useState } from "react";
import { Building2, MapPinned, Users, Warehouse, Heart, Flame, TrendingUp, TrendingDown, ArrowRight, Flag, Shield, Swords, AlertTriangle, Coins } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useLocale } from "@/hooks/useLocale";
import type { Translations } from "@/lib/i18n/types";

interface OperationsPanelProps {
  // Org
  orgName: string | null;
  orgTier: number | null;
  orgMorale: number | null;
  orgHeat: number | null;
  orgIncome: number | null;
  managementStyle: string | null;
  currencyAmount?: number | null;
  currencyName?: string | null;
  // Territory
  controlledLocations: any[] | null;
  contestedLocations: any[] | null;
  rivalFactions: any[] | null;
  territoryScale: string | null;
  // Resources
  resourcePools?: Record<string, any> | null;
  resourceProduction?: Record<string, any> | null;
  resourceUpkeep?: Record<string, number> | null;
  resourceNet?: Record<string, number> | null;
  // Manpower
  manpowerRoster?: Record<string, any> | null;
}

const TIER_LABELS: Record<number, keyof Translations> = {
  1: "play_tier_street", 2: "play_tier_established", 3: "play_tier_major", 4: "play_tier_dominant", 5: "play_tier_undisputed",
};

const STYLE_LABELS: Record<string, keyof Translations> = {
  hierarchical: "play_style_hierarchical", cell: "play_style_cell", feudal: "play_style_feudal",
};

export function OperationsPanel(props: OperationsPanelProps) {
  const { t } = useLocale();
  const {
    orgName, orgTier, orgMorale, orgHeat, orgIncome, managementStyle,
    currencyAmount, currencyName,
    controlledLocations, contestedLocations, rivalFactions, territoryScale,
    resourcePools, resourceProduction, resourceUpkeep, resourceNet,
    manpowerRoster,
  } = props;

  const controlled = controlledLocations || [];
  const contested = contestedLocations || [];
  const rivals = rivalFactions || [];
  const roster = manpowerRoster || {};
  const pools = resourcePools || {};
  const prod = resourceProduction || {};
  const upkeep = resourceUpkeep || {};
  const net = resourceNet || {};

  const rosterEntries = Object.entries(roster).filter(([, b]) => b && typeof b === "object");
  const totalPersonnel = rosterEntries.reduce((s, [, b]: any) => s + (typeof b?.count === "number" ? b.count : 0), 0);
  const poolEntries = Object.entries(pools);

  return (
    <div {...storyElement("operations-panel")}>
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full grid grid-cols-4 h-8">
          <TabsTrigger value="overview" className="text-[10px] px-1">{t.play_ops_overview}</TabsTrigger>
          <TabsTrigger value="territory" className="text-[10px] px-1">{t.play_ops_territory}</TabsTrigger>
          <TabsTrigger value="workforce" className="text-[10px] px-1">{t.play_ops_workforce}</TabsTrigger>
          <TabsTrigger value="resources" className="text-[10px] px-1">{t.play_ops_resources}</TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="space-y-4 mt-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-bold text-foreground">{orgName || t.play_org_unnamed}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {managementStyle && <Badge variant="outline" className="text-[10px]">{t[STYLE_LABELS[managementStyle]] || managementStyle}</Badge>}
            </div>
          </div>

          <Separator />

          {/* Treasury */}
          {currencyAmount != null && (
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Coins className="w-3 h-3 text-yellow-500" /> {t.play_org_treasury}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-bold text-foreground">{currencyAmount} {currencyName || "Gold"}</span>
                {(orgIncome ?? 0) > 0 && (
                  <span className="text-[10px] text-green-400 flex items-center gap-0.5">
                    <TrendingUp className="w-2.5 h-2.5" />+{orgIncome}/turn
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Tier */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t.play_org_tier}</span>
              <span className="font-bold text-foreground">{orgTier ?? 1} — {t[TIER_LABELS[orgTier ?? 1]] || `${t.play_org_tier} ${orgTier}`}</span>
            </div>
            <Progress value={((orgTier ?? 1) / 5) * 100} className="h-1.5" />
          </div>

          {/* Morale */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Heart className={`w-3 h-3 ${(orgMorale ?? 50) < 40 ? "text-destructive" : (orgMorale ?? 50) > 70 ? "text-green-400" : "text-muted-foreground"}`} />
                {t.play_org_morale}
              </span>
              <span className={`font-medium ${(orgMorale ?? 50) < 40 ? "text-destructive" : (orgMorale ?? 50) > 70 ? "text-green-400" : "text-foreground"}`}>
                {orgMorale ?? 50}/100
              </span>
            </div>
            <Progress value={orgMorale ?? 50} className="h-1.5" />
          </div>

          {/* Heat */}
          {(orgHeat ?? 0) > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Flame className={`w-3 h-3 ${(orgHeat ?? 0) > 60 ? "text-destructive" : (orgHeat ?? 0) > 30 ? "text-orange-400" : "text-muted-foreground"}`} />
                  {t.play_org_heat}
                </span>
                <span className={`font-medium ${(orgHeat ?? 0) > 60 ? "text-destructive" : (orgHeat ?? 0) > 30 ? "text-orange-400" : "text-foreground"}`}>
                  {orgHeat}/100
                </span>
              </div>
              <Progress value={orgHeat ?? 0} className="h-1.5" />
            </div>
          )}

          {/* Quick stats */}
          <Separator />
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <div className="text-lg font-bold text-foreground">{controlled.length}</div>
              <div className="text-[10px] text-muted-foreground">{t.play_org_territories}</div>
            </div>
            <div>
              <div className="text-lg font-bold text-foreground">{totalPersonnel}</div>
              <div className="text-[10px] text-muted-foreground">{t.play_org_personnel}</div>
            </div>
            <div>
              <div className="text-lg font-bold text-foreground">{poolEntries.length}</div>
              <div className="text-[10px] text-muted-foreground">{t.play_org_resources}</div>
            </div>
          </div>
        </TabsContent>

        {/* ── Territory ── */}
        <TabsContent value="territory" className="space-y-4 mt-3">
          {territoryScale && <Badge variant="outline" className="text-[10px] capitalize">{territoryScale} scale</Badge>}

          {controlled.length === 0 && contested.length === 0 && rivals.length === 0 && (
            <div className="text-xs text-muted-foreground/50 text-center py-4">
              {t.play_org_no_territory}
            </div>
          )}

          {controlled.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                <Flag className="w-3 h-3" /> {t.play_org_controlled} ({controlled.length})
              </div>
              {controlled.map((loc: any, i: number) => (
                <div key={loc.id || i} className="flex items-center justify-between text-xs border border-border rounded-md px-2 py-1.5">
                  <span className="font-medium text-foreground truncate">{loc.name || loc.id}</span>
                  <div className="flex items-center gap-1.5">
                    {loc.production_type && <Badge variant="secondary" className="text-[10px]">{loc.production_type}</Badge>}
                    {loc.defense_level && (
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <Shield className="w-2.5 h-2.5" />{loc.defense_level}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {contested.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <Swords className="w-3 h-3 text-destructive" /> {t.play_org_contested} ({contested.length})
                </div>
                {contested.map((loc: any, i: number) => (
                  <div key={loc.id || i} className="flex items-center justify-between text-xs border border-destructive/30 rounded-md px-2 py-1.5">
                    <span className="font-medium text-foreground truncate">{loc.name || loc.id}</span>
                    <div className="flex flex-wrap gap-1">
                      {(loc.claimants || []).map((c: any, ci: number) => (
                        <Badge key={ci} variant="outline" className="text-[10px]">{c.faction}: {c.strength}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {rivals.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> {t.play_org_rival_factions} ({rivals.length})
                </div>
                {rivals.map((f: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-foreground">{f.name || `Faction ${i + 1}`}</span>
                    <div className="flex items-center gap-1.5">
                      {f.territory_count != null && <span className="text-[10px] text-muted-foreground">{f.territory_count} territories</span>}
                      {f.threat_level && (
                        <Badge variant={f.threat_level === "high" ? "destructive" : "secondary"} className="text-[10px]">{f.threat_level}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ── Workforce ── */}
        <TabsContent value="workforce" className="space-y-4 mt-3">
          {rosterEntries.length === 0 ? (
            <div className="text-xs text-muted-foreground/50 text-center py-4">
              {t.play_org_no_personnel}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                <Users className="w-3 h-3" /> {t.play_org_personnel} ({totalPersonnel} total)
              </div>
              {rosterEntries.map(([id, batch]) => {
                const b = batch as any;
                const linkedCount = b.linked_npc_ids?.length || 0;
                return (
                  <div key={id} className="flex items-center justify-between text-xs gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span>{b.icon || "👤"}</span>
                      <span className="font-medium truncate">{b.role || id}</span>
                      {b.experience && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">{b.experience}</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="font-bold text-foreground">{b.count ?? 0}</span>
                      {linkedCount > 0 && <span className="text-[9px] text-muted-foreground">({linkedCount} named)</span>}
                      {b.assigned_location_id && <Badge variant="secondary" className="text-[9px] px-1 py-0">📍</Badge>}
                      {b.status && b.status !== "active" && <Badge variant="secondary" className="text-[9px] px-1 py-0">{b.status}</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Resources ── */}
        <TabsContent value="resources" className="space-y-4 mt-3">
          {/* Currency */}
          {currencyAmount != null && currencyAmount > 0 && (
            <div className="flex items-center justify-between px-2 py-1.5 rounded bg-muted/30 border border-border/30">
              <span className="text-xs text-muted-foreground flex items-center gap-1">💰 {currencyName || "Gold"}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-foreground">{currencyAmount}</span>
                {(orgIncome ?? 0) > 0 && (
                  <span className="text-[10px] text-green-400 flex items-center gap-0.5">
                    <TrendingUp className="w-2.5 h-2.5" />+{orgIncome}/turn
                  </span>
                )}
              </div>
            </div>
          )}

          {poolEntries.length === 0 && Object.keys(prod).length === 0 && (
            <div className="text-xs text-muted-foreground/50 text-center py-4">
              {t.play_org_no_resources}
            </div>
          )}

          {/* Stockpile */}
          {poolEntries.length > 0 && (() => {
            const maxRes = Math.max(1, ...poolEntries.map(([, p]: any) => p?.amount || 0));
            return (
              <div className="space-y-3">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <Warehouse className="w-3 h-3" /> {t.play_org_stockpile}
                </div>
                {poolEntries.map(([name, pool]: any) => {
                  const netVal = net[name] || 0;
                  const pct = ((pool?.amount || 0) / maxRes) * 100;
                  return (
                    <div key={name} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium capitalize flex items-center gap-1 truncate" title={pool?.description}>
                          {pool?.icon || "📦"} {name}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-bold text-foreground">{pool?.amount ?? 0}</span>
                          {netVal !== 0 && (
                            <span className={`text-[10px] flex items-center gap-0.5 ${netVal > 0 ? "text-green-400" : "text-destructive"}`}>
                              {netVal > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                              {netVal > 0 ? "+" : ""}{netVal}/turn
                            </span>
                          )}
                        </div>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Production */}
          {Object.keys(prod).length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" /> {t.play_org_production}
                </div>
                {(Object.entries(prod) as [string, any][]).map(([locId, p]) => (
                  <div key={locId} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{p.location_name || locId}</span>
                    <ArrowRight className="w-3 h-3 shrink-0" />
                    <Badge variant="outline" className="text-[10px] px-1.5">+{p.amount} {p.type}</Badge>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Upkeep */}
          {Object.keys(upkeep).some((k) => (upkeep[k] || 0) > 0) && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <TrendingDown className="w-3 h-3" /> {t.play_org_upkeep}
                </div>
                {Object.entries(upkeep).filter(([, v]) => (v || 0) > 0).map(([resType, cost]) => (
                  <div key={resType} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="capitalize">{resType}</span>
                    <span className="text-destructive">-{cost}/turn</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
